import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { INGREDIENT_CATEGORIES } from '@fewerbags/schema';
import type { IngredientCategory, IngredientDto } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Field } from '../Field';
import { SelectField } from '../SelectField';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { CATEGORY_LABEL } from '../../lib/ingredientDisplay';
import { UNIT_OPTIONS } from '../../lib/units';

const CATEGORY_OPTIONS = [
  { value: '', label: 'Choose a category…' },
  ...INGREDIENT_CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABEL[c] })),
];

const LOCATIONS = [
  { key: 'pantry', label: 'Pantry' },
  { key: 'fridge', label: 'Fridge' },
  { key: 'freezer', label: 'Freezer' },
] as const;

/**
 * Fill-if-null editor for one ingredient. Any household may set a field that
 * is still empty; changing a field that already has a value requires admin
 * (the API rejects the whole request with 409 otherwise).
 */
export function ImproveEntryForm({
  ingredient,
  isAdmin,
}: {
  ingredient: IngredientDto;
  isAdmin: boolean;
}) {
  const qc = useQueryClient();
  const [category, setCategory] = useState('');
  const [defaultUnit, setDefaultUnit] = useState('count');
  const [density, setDensity] = useState('');
  const [unitWeight, setUnitWeight] = useState('');
  const [shelf, setShelf] = useState<Record<'pantry' | 'fridge' | 'freezer', string>>({
    pantry: '',
    fridge: '',
    freezer: '',
  });

  useEffect(() => {
    setCategory(ingredient.category ?? '');
    setDefaultUnit(ingredient.defaultUnit);
    setDensity(ingredient.densityGPerMl != null ? String(ingredient.densityGPerMl) : '');
    setUnitWeight(ingredient.avgUnitWeightG != null ? String(ingredient.avgUnitWeightG) : '');
    setShelf({
      pantry:
        ingredient.shelfLifeDays.pantry != null ? String(ingredient.shelfLifeDays.pantry) : '',
      fridge:
        ingredient.shelfLifeDays.fridge != null ? String(ingredient.shelfLifeDays.fridge) : '',
      freezer:
        ingredient.shelfLifeDays.freezer != null ? String(ingredient.shelfLifeDays.freezer) : '',
    });
  }, [ingredient]);

  const update = useMutation({
    mutationFn: (input: Parameters<typeof api.ingredients.update>[1]) =>
      api.ingredients.update(ingredient.id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.ingredientDetail(ingredient.id) }),
  });

  const categoryLocked = !isAdmin && ingredient.category !== null;
  const unitLocked = !isAdmin && ingredient.defaultUnit !== 'count';
  const densityLocked = !isAdmin && ingredient.densityGPerMl !== null;
  const unitWeightLocked = !isAdmin && ingredient.avgUnitWeightG !== null;
  const shelfLocked = {
    pantry: !isAdmin && ingredient.shelfLifeDays.pantry !== null,
    fridge: !isAdmin && ingredient.shelfLifeDays.fridge !== null,
    freezer: !isAdmin && ingredient.shelfLifeDays.freezer !== null,
  };

  function parseShelfField(key: 'pantry' | 'fridge' | 'freezer'): number | null | undefined {
    const initial =
      ingredient.shelfLifeDays[key] != null ? String(ingredient.shelfLifeDays[key]) : '';
    const current = shelf[key];
    if (current === initial) return undefined; // unchanged
    if (current.trim() === '') return null;
    const n = Number(current);
    return Number.isFinite(n) ? n : undefined;
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const patch: NonNullable<Parameters<typeof api.ingredients.update>[1]> = {};
    if (category && category !== (ingredient.category ?? '')) {
      patch.category = category as IngredientCategory;
    }
    if (defaultUnit !== ingredient.defaultUnit) {
      patch.defaultUnit = defaultUnit as IngredientDto['defaultUnit'];
    }
    if (density.trim() !== '' && Number(density) !== ingredient.densityGPerMl) {
      const n = Number(density);
      if (Number.isFinite(n)) patch.densityGPerMl = n;
    }
    if (unitWeight.trim() !== '' && Number(unitWeight) !== ingredient.avgUnitWeightG) {
      const n = Number(unitWeight);
      if (Number.isFinite(n)) patch.avgUnitWeightG = n;
    }
    const shelfPatch: Record<string, number | null> = {};
    for (const loc of ['pantry', 'fridge', 'freezer'] as const) {
      const v = parseShelfField(loc);
      if (v !== undefined) shelfPatch[loc] = v;
    }
    if (Object.keys(shelfPatch).length > 0) {
      patch.shelfLifeDays = shelfPatch;
    }
    if (Object.keys(patch).length === 0) return;
    update.mutate(patch);
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
      {!isAdmin ? (
        <p className="text-xs text-muted">
          You can fill in fields that are still empty. Changing a value that’s already set needs an
          administrator.
        </p>
      ) : (
        <p className="text-xs text-muted">
          As an administrator you can change any field below (changes are logged).
        </p>
      )}
      <SelectField
        id="ingredient-category"
        label="Category"
        options={CATEGORY_OPTIONS}
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        disabled={categoryLocked}
      />
      <SelectField
        id="ingredient-default-unit"
        label="Default unit"
        options={UNIT_OPTIONS}
        value={defaultUnit}
        onChange={(e) => setDefaultUnit(e.target.value)}
        disabled={unitLocked}
      />
      <Field
        id="ingredient-density"
        label="Density (g per ml, for mass ↔ volume conversion)"
        type="number"
        inputMode="decimal"
        step="any"
        min="0"
        max="30"
        value={density}
        onChange={(e) => setDensity(e.target.value)}
        disabled={densityLocked}
        placeholder="e.g. 1.03 for milk"
      />
      <Field
        id="ingredient-unit-weight"
        label="Average weight of one (g, for count ↔ weight conversion)"
        type="number"
        inputMode="decimal"
        step="any"
        min="0"
        max="10000"
        value={unitWeight}
        onChange={(e) => setUnitWeight(e.target.value)}
        disabled={unitWeightLocked}
        placeholder="e.g. 150 for an onion"
      />
      <div className="grid grid-cols-3 gap-3">
        {LOCATIONS.map(({ key, label }) => (
          <Field
            key={key}
            id={`ingredient-shelf-${key}`}
            label={`${label} (days)`}
            type="number"
            inputMode="numeric"
            min="0"
            max="3650"
            value={shelf[key]}
            onChange={(e) => setShelf((s) => ({ ...s, [key]: e.target.value }))}
            disabled={shelfLocked[key]}
          />
        ))}
      </div>
      {update.isError ? <Alert tone="error">{messageForError(update.error)}</Alert> : null}
      {update.isSuccess ? <Alert tone="success">Saved.</Alert> : null}
      <Button type="submit" disabled={update.isPending}>
        Save
      </Button>
    </form>
  );
}
